if (!customElements.get('variant-matrix')) {
  customElements.define('variant-matrix', class VariantMatrix extends HTMLElement {
    constructor() {
      super();

      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.sectionId = this.dataset.sectionId;
      this.productId = this.dataset.productId;
      this._wishlistCache = null; // Cache for wishlist

      this.initEventListeners();
      this.restoreWishlistState();
    }

    initEventListeners() {
      // カートボタンのイベントリスナー（primaryボタン）
      this.querySelectorAll('.c_variant-matrix__button.button--primary').forEach(button => {
        button.addEventListener('click', this.handleAddToCart.bind(this));
      });

      // 再入荷通知ボタンのイベントリスナー（secondaryボタン）
      this.querySelectorAll('.c_variant-matrix__button.button--secondary').forEach(button => {
        button.addEventListener('click', this.handleBackInStock.bind(this));
      });

      // お気に入りボタンのイベントリスナー
      this.querySelectorAll('.c_variant-matrix__wishlist').forEach(button => {
        button.addEventListener('click', this.handleWishlistToggle.bind(this));
      });
    }

    async handleAddToCart(event) {
      const button = event.currentTarget;
      const variantId = button.dataset.variantId;

      if (!variantId || button.classList.contains('loading')) {
        return;
      }

      const spinner = button.querySelector('.loading-overlay__spinner');

      try {
        // ローディング状態を設定
        button.classList.add('loading');
        button.setAttribute('aria-busy', 'true');
        if (spinner) spinner.classList.remove('hidden');

        // FormDataを使用してリクエスト
        const body = JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        });

        const cartUrl = window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js';

        const response = await fetch(cartUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: body
        });

        if (!response.ok) {
          const errorData = await response.json();
          const errorMessage = this.dataset.errorMessage || 'カートに追加できませんでした';
          throw new Error(errorData.description || errorMessage);
        }

        const responseData = await response.json();

        // カスタムイベントを発火
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { variant_id: variantId }
        }));

        // cart-drawer/cart-notificationが存在しない場合はカートページに遷移
        if (!this.cart) {
          window.location = window.Shopify.routes.root + 'cart';
          return;
        }

        // セクションを更新
        this.renderContents(responseData);

        // カートドロワーを開く
        if (this.cart && typeof this.cart.open === 'function') {
          this.cart.open();
        }

      } catch (error) {
        console.error('Error adding to cart:', error);
        const tryAgainMessage = this.dataset.tryAgainMessage || 'カートに追加できませんでした。もう一度お試しください。';
        alert(error.message || tryAgainMessage);
      } finally {
        // 常にローディング状態を解除
        button.classList.remove('loading');
        button.setAttribute('aria-busy', 'false');
        if (spinner) spinner.classList.add('hidden');
      }
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'cart-drawer',
          section: document.getElementById('cart-drawer') ? 'cart-drawer' : null,
          selector: '#CartDrawer'
        }
      ].filter(section => section.section !== null);
    }

    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        if (!parsedState.sections || !parsedState.sections[section.section]) {
          return;
        }

        const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);

        if (elementToReplace) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        }
      });
    }

    getSectionInnerHTML(html, selector) {
      // Parse HTML from Shopify-controlled cart response (trusted source)
      const element = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector);

      // Null safety check
      if (!element) {
        console.warn(`Element not found for selector: ${selector}`);
        return '';
      }

      return element.innerHTML;
    }

    handleBackInStock(event) {
      const button = event.currentTarget;
      const variantId = button.dataset.variantId;

      // Back In Stock アプリが利用可能かチェック
      if (window.BIS && window.BIS.popup && typeof window.BIS.popup.show === 'function') {
        // Back In Stock アプリのポップアップを表示
        window.BIS.popup.show({ variantId: variantId });
      } else {
        // フォールバック: 既存のモーダル処理
        const notifyButton = document.querySelector(`#notify-button-${this.sectionId}`);

        if (notifyButton) {
          // modal-opener内のbuttonを取得してクリック
          const modalOpener = notifyButton.querySelector('modal-opener');
          if (modalOpener) {
            const openButton = modalOpener.querySelector('button');
            if (openButton) {
              // バリアントIDをセット（モーダル内にinputがある場合）
              const modalId = modalOpener.getAttribute('data-modal');
              if (modalId) {
                const modal = document.querySelector(modalId);
                if (modal) {
                  const variantInput = modal.querySelector('input[name="variant_id"]');
                  if (variantInput) {
                    variantInput.value = variantId;
                  }
                }
              }
              // modal-openerのbuttonをクリック
              openButton.click();
            }
          }
        } else {
          // モーダルが存在しない場合はデフォルトの処理
          console.log('Back in stock notification requested for variant:', variantId);
          const notifyMessage = this.dataset.notifyMessage || '再入荷通知を登録するにはメールアドレスを入力してください。';
          alert(notifyMessage);
        }
      }
    }

    handleWishlistToggle(event) {
      const button = event.currentTarget;
      const variantId = button.dataset.variantId;
      const productId = button.dataset.productId;
      const productUrl = button.dataset.productUrl;
      const productTitle = button.dataset.productTitle;
      const variantTitle = button.dataset.variantTitle;
      const productImage = button.dataset.productImage;
      const variantPrice = button.dataset.variantPrice;

      // Swym Wishlist Plus APIが利用可能かチェック
      if (window._swat && typeof window._swat.fetch === 'function') {
        this.handleSwymWishlist(button, {
          variantId,
          productId,
          productUrl,
          productTitle,
          variantTitle,
          productImage,
          variantPrice
        });
      } else {
        // フォールバック: ローカルストレージ実装
        this.handleLocalStorageWishlist(button, variantId);
      }
    }

    /**
     * Swym Wishlist Plus API を使用したウィッシュリスト処理
     */
    handleSwymWishlist(button, productData) {
      const { variantId, productId, productUrl, productTitle, variantTitle, productImage, variantPrice } = productData;

      // Swym用の商品データオブジェクト
      const product = {
        epi: variantId,
        empi: productId,
        du: productUrl,
        dt: productTitle,
        // バリアント情報を追加
        iu: productImage,
        pr: variantPrice / 100, // 価格を通貨単位に変換
        variants: [{
          epi: variantId,
          iu: productImage,
          pr: variantPrice / 100
        }]
      };

      // Swymのウィッシュリスト状態を取得
      window._swat.fetch((products) => {
        const isInWishlist = products.some(p => p.epi == variantId);

        if (isInWishlist) {
          // ウィッシュリストから削除
          window._swat.removeFromWishList(product, () => {
            button.classList.remove('is-active', 'swym-added');
            button.setAttribute('aria-label', 'Add to wishlist');
            button.setAttribute('aria-pressed', 'false');
          }, (error) => {
            console.warn('Swym remove error, using fallback:', error);
            // エラー時はLocalStorageにフォールバック
            this.handleLocalStorageWishlist(button, variantId);
          });
        } else {
          // ウィッシュリストに追加
          window._swat.addToWishList(product, () => {
            button.classList.add('is-active', 'swym-added');
            button.setAttribute('aria-label', 'Remove from wishlist');
            button.setAttribute('aria-pressed', 'true');
          }, (error) => {
            console.warn('Swym add error, using fallback:', error);
            // エラー時はLocalStorageにフォールバック
            this.handleLocalStorageWishlist(button, variantId);
          });
        }
      }, (error) => {
        console.warn('Swym fetch error, using fallback:', error);
        // エラー時はLocalStorageにフォールバック
        this.handleLocalStorageWishlist(button, variantId);
      });
    }

    /**
     * フォールバック: LocalStorage実装
     */
    handleLocalStorageWishlist(button, variantId) {
      let wishlist = this.getWishlist();
      const isInWishlist = wishlist.includes(variantId);

      if (isInWishlist) {
        wishlist = wishlist.filter(id => id !== variantId);
        button.classList.remove('is-active');
        button.setAttribute('aria-label', 'Add to wishlist');
        button.setAttribute('aria-pressed', 'false');
      } else {
        wishlist.push(variantId);
        button.classList.add('is-active');
        button.setAttribute('aria-label', 'Remove from wishlist');
        button.setAttribute('aria-pressed', 'true');
      }

      this.saveWishlist(wishlist);

      // カスタムイベントを発火
      this.dispatchEvent(new CustomEvent('wishlist:changed', {
        bubbles: true,
        detail: { variantId, isInWishlist: !isInWishlist }
      }));
    }

    getWishlist() {
      // Return cached wishlist if available
      if (this._wishlistCache !== null) {
        return this._wishlistCache;
      }

      try {
        const wishlist = localStorage.getItem('shopify_wishlist');
        this._wishlistCache = wishlist ? JSON.parse(wishlist) : [];
        return this._wishlistCache;
      } catch (error) {
        console.error('Error reading wishlist:', error);
        this._wishlistCache = [];
        return [];
      }
    }

    saveWishlist(wishlist) {
      try {
        this._wishlistCache = wishlist; // Update cache
        localStorage.setItem('shopify_wishlist', JSON.stringify(wishlist));
      } catch (error) {
        console.error('Error saving wishlist:', error);
      }
    }

    // 初期化時にウィッシュリストの状態を復元
    restoreWishlistState() {
      if (window._swat && typeof window._swat.fetch === 'function') {
        // Swym Wishlist Plus から状態を取得
        window._swat.fetch((products) => {
          const wishlistVariantIds = products.map(p => String(p.epi));

          this.querySelectorAll('.c_variant-matrix__wishlist').forEach(button => {
            const variantId = button.dataset.variantId;
            if (wishlistVariantIds.includes(variantId)) {
              button.classList.add('is-active', 'swym-added');
              button.setAttribute('aria-label', 'Remove from wishlist');
              button.setAttribute('aria-pressed', 'true');
            }
          });
        }, (error) => {
          console.error('Swym fetch error on init:', error);
          // エラー時はLocalStorageにフォールバック
          this.restoreFromLocalStorage();
        });
      } else {
        // フォールバック: LocalStorage から復元
        this.restoreFromLocalStorage();
      }
    }

    restoreFromLocalStorage() {
      const wishlist = this.getWishlist();

      this.querySelectorAll('.c_variant-matrix__wishlist').forEach(button => {
        const variantId = button.dataset.variantId;
        if (wishlist.includes(variantId)) {
          button.classList.add('is-active');
          button.setAttribute('aria-label', 'Remove from wishlist');
          button.setAttribute('aria-pressed', 'true');
        }
      });
    }
  });
}
